import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { experienceApi } from '@internal/api/experience';
import { profileApi } from '@internal/api/profile';
import { clearEnrichmentJob } from '@internal/components/app/components/enrichment-watcher';
import {
  ENRICHMENT_JOB_STORED_EVENT,
  readEnrichmentJob,
} from '@internal/components/app/components/enrichment-watcher/enrichment-job-storage';
import ProcessingLoader from '@internal/components/app/components/processing-loader';
import { EnrichmentResult, EnrichmentStatus } from '@internal/types/api';

const STEPS = [
  'Pulling your LinkedIn data',
  'Mapping your experience',
  'Identifying your core skills',
  'Almost there',
  'Ready',
] as const;

const PARSED_QUERY_KEYS = [
  ['profile'],
  ['projects'],
  ['educations'],
  ['specializations'],
  ['application-state'],
  ['candidate-roles'],
];

const POLL_INTERVAL = 5000;
const NO_JOB_FALLBACK = 20000;
const STUCK_JOB_FALLBACK = 60000;

const isTerminal = (status?: EnrichmentStatus) =>
  status === EnrichmentStatus.Finished ||
  status === EnrichmentStatus.FinishedWithErrors;

const ParsingStep: React.FC<{
  apiKey: string;
  onDone: () => void;
}> = ({ apiKey, onDone }) => {
  const queryClient = useQueryClient();
  const [pendingJob, setPendingJob] = useState(() => readEnrichmentJob());
  const [fallbackDone, setFallbackDone] = useState(false);
  const [parsed, setParsed] = useState(false);

  useEffect(() => {
    const sync = () => setPendingJob(readEnrichmentJob());
    window.addEventListener(ENRICHMENT_JOB_STORED_EVENT, sync);
    return () => window.removeEventListener(ENRICHMENT_JOB_STORED_EVENT, sync);
  }, []);

  const jobId =
    pendingJob?.apiKey === apiKey ? pendingJob?.jobId ?? null : null;
  const awaitingJobId = pendingJob?.apiKey === apiKey && !jobId;

  useEffect(() => {
    if (jobId || awaitingJobId) return undefined;
    const timer = setTimeout(() => setFallbackDone(true), NO_JOB_FALLBACK);
    return () => clearTimeout(timer);
  }, [jobId, awaitingJobId]);

  useEffect(() => {
    const timer = setTimeout(() => setFallbackDone(true), STUCK_JOB_FALLBACK);
    return () => clearTimeout(timer);
  }, []);

  const { enrichmentJob } = profileApi.getEnrichmentJob.useQuery(
    jobId ? { apiKey, jobId } : undefined,
    {
      enabled: Boolean(jobId),
      refetchInterval: query => {
        const status = (query.state.data as EnrichmentResult | null)?.status;
        return isTerminal(status) ? false : POLL_INTERVAL;
      },
    }
  );

  const status = enrichmentJob?.status;

  useEffect(() => {
    if (!isTerminal(status) || parsed) return;
    setParsed(true);
    clearEnrichmentJob();
    PARSED_QUERY_KEYS.forEach(queryKey =>
      queryClient.invalidateQueries({ queryKey })
    );
  }, [status, parsed, queryClient]);

  const dataPollEnabled = awaitingJobId && !parsed && !fallbackDone;
  const { projects = [] } = experienceApi.getProjects.useQuery(apiKey, {
    enabled: dataPollEnabled,
    refetchInterval: POLL_INTERVAL,
  });
  const { educations = [] } = experienceApi.getEducations.useQuery(apiKey, {
    enabled: dataPollEnabled,
    refetchInterval: POLL_INTERVAL,
  });

  useEffect(() => {
    if (!dataPollEnabled) return;
    if (!projects.length && !educations.length) return;
    setParsed(true);
    clearEnrichmentJob();
    PARSED_QUERY_KEYS.forEach(queryKey =>
      queryClient.invalidateQueries({ queryKey })
    );
  }, [dataPollEnabled, projects, educations, queryClient]);

  const redirectedRef = useRef(false);
  useEffect(() => {
    if (!fallbackDone || parsed || redirectedRef.current) return;
    redirectedRef.current = true;
    PARSED_QUERY_KEYS.forEach(queryKey =>
      queryClient.invalidateQueries({ queryKey })
    );
    onDone();
  }, [fallbackDone, parsed, queryClient, onDone]);

  return (
    <>
      <Typography variant="h4">Building your profile…</Typography>
      <Typography variant="body3" sx={{ mt: 1, color: 'text.primary' }}>
        Pulling the details so we don&apos;t ask you the same things twice.
      </Typography>

      <Box sx={{ mt: 3 }}>
        <ProcessingLoader
          steps={STEPS}
          isDone={parsed}
          onComplete={onDone}
          borderless
        />
      </Box>
    </>
  );
};

export default ParsingStep;
